import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "form", "errors", "logoPreview", "logoInput", "cropArea", "cropImage",
    "cropX", "cropY", "cropW", "cropH", "submitBtn",
    "countrySelect", "stateSelect", "removeLogo", "removeLogoBtn"
  ]
  static values = { url: String, noLogoLabel: String }

  connect() {
    this._originalBtnText = this.submitBtnTarget.textContent
  }

  disconnect() {
    this._destroyCropper()
  }

  submit(event) {
    if (event) event.preventDefault()

    // Auto-apply crop if the user hasn't done it yet
    if (this.cropper) {
      this.applyCrop()
    }

    const formData = new FormData(this.formTarget)

    // Only include logo in the request when a new file was selected
    const logoFile = this.logoInputTarget.files[0]
    if (logoFile) {
      formData.append("company[logo]", logoFile)
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    this.submitBtnTarget.disabled = true
    this.submitBtnTarget.textContent = this.submitBtnTarget.dataset.savingText || "Saving..."

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: formData
    })
    .then(response => response.json().then(data => ({ ok: response.ok, data })))
    .then(({ ok, data }) => {
      if (ok && data.success) {
        this._updateUI(data.name)
        this._showFlash("notice", data.message)
        this._closeModal()
      } else {
        this._showErrors(data.errors || ["Update failed"])
      }
    })
    .catch(() => {
      this._showErrors(["A network error occurred. Please try again."])
    })
    .finally(() => {
      this.submitBtnTarget.disabled = false
      this.submitBtnTarget.textContent = this._originalBtnText
    })
  }

  selectLogo() {
    const file = this.logoInputTarget.files[0]
    if (!file) return

    // A freshly picked logo cancels any pending removal.
    this._removedLogo = false
    if (this.hasRemoveLogoTarget) this.removeLogoTarget.value = ""
    if (this.hasRemoveLogoBtnTarget) this.removeLogoBtnTarget.classList.remove("hidden")

    const reader = new FileReader()
    reader.onload = (e) => {
      this.cropImageTarget.src = e.target.result
      this.cropAreaTarget.classList.remove("hidden")
      this._initCropper()
    }
    reader.readAsDataURL(file)
  }

  // Mark the existing logo for removal and reflect it in the UI. The actual
  // delete happens server-side on Update (companies#update reads remove_logo).
  removeLogo() {
    this._removedLogo = true
    if (this.hasRemoveLogoTarget) this.removeLogoTarget.value = "1"

    // Cancel any pending new-file pick so removal wins.
    this.logoInputTarget.value = ""
    this._previewDataUrl = null
    this.cropAreaTarget.classList.add("hidden")
    this._destroyCropper()

    // Swap the preview for the "No logo" placeholder, keeping the target so a
    // later Edit Logo pick still updates it in place.
    const placeholder = document.createElement("div")
    placeholder.className = "h-12 w-32 border border-dashed border-border-light rounded flex items-center justify-center text-text-muted text-xs"
    placeholder.textContent = this.hasNoLogoLabelValue ? this.noLogoLabelValue : "No logo"
    placeholder.dataset.companyFormTarget = "logoPreview"
    this.logoPreviewTarget.replaceWith(placeholder)

    if (this.hasRemoveLogoBtnTarget) this.removeLogoBtnTarget.classList.add("hidden")
  }

  applyCrop() {
    if (!this.cropper) return

    const data = this.cropper.getData(true)
    this.cropXTarget.value = Math.round(data.x)
    this.cropYTarget.value = Math.round(data.y)
    this.cropWTarget.value = Math.round(data.width)
    this.cropHTarget.value = Math.round(data.height)

    // Update preview with cropped canvas
    const canvas = this.cropper.getCroppedCanvas({ maxWidth: 400 })
    if (canvas) {
      this._previewDataUrl = canvas.toDataURL()
      const preview = this.logoPreviewTarget
      if (preview.tagName === "IMG") {
        preview.src = this._previewDataUrl
      } else {
        const img = document.createElement("img")
        img.src = this._previewDataUrl
        img.alt = "Logo"
        img.className = "h-12 max-w-[220px] object-contain border border-border-light rounded p-1"
        img.dataset.companyFormTarget = "logoPreview"
        preview.replaceWith(img)
      }
    }

    this.cropAreaTarget.classList.add("hidden")
    this._destroyCropper()
  }

  countryChanged() {
    const code = this.countrySelectTarget.value
    const select = this.stateSelectTarget
    select.innerHTML = '<option value=""></option>'

    if (!code) return

    fetch(`/locations/subregions?country_code=${code}`, {
      headers: { "Accept": "application/json" }
    })
    .then(r => r.json())
    .then(subregions => {
      subregions.forEach(sr => {
        const opt = document.createElement("option")
        opt.value = sr.code
        opt.textContent = sr.name
        select.appendChild(opt)
      })
    })
  }

  zoomIn() {
    if (this.cropper) this.cropper.zoom(0.1)
  }

  zoomOut() {
    if (this.cropper) this.cropper.zoom(-0.1)
  }

  // ── Private ──

  async _initCropper() {
    this._destroyCropper()

    const { default: Cropper } = await import("cropperjs")

    // Inject Cropper.js CSS if not already loaded
    if (!document.getElementById("cropperjs-css")) {
      const link = document.createElement("link")
      link.id = "cropperjs-css"
      link.rel = "stylesheet"
      link.href = "https://cdnjs.cloudflare.com/ajax/libs/cropperjs/1.6.2/cropper.min.css"
      document.head.appendChild(link)
    }

    // Make crop handles larger and more visible
    if (!document.getElementById("cropperjs-overrides")) {
      const style = document.createElement("style")
      style.id = "cropperjs-overrides"
      style.textContent = `
        .cropper-point {
          width: 8px !important;
          height: 8px !important;
          background-color: #3b82f6 !important;
          opacity: 1 !important;
          border-radius: 1px;
        }
        .cropper-point.point-nw { top: -4px; left: -4px; }
        .cropper-point.point-ne { top: -4px; right: -4px; }
        .cropper-point.point-sw { bottom: -4px; left: -4px; }
        .cropper-point.point-se { bottom: -4px; right: -4px; width: 8px !important; height: 8px !important; }
        .cropper-point.point-n { top: -4px; }
        .cropper-point.point-s { bottom: -4px; }
        .cropper-point.point-w { left: -4px; }
        .cropper-point.point-e { right: -4px; }
        .cropper-line { background-color: #3b82f6 !important; opacity: 0.3 !important; }
      `
      document.head.appendChild(style)
    }

    this.cropper = new Cropper(this.cropImageTarget, {
      viewMode: 1,
      autoCropArea: 1,
      responsive: true
    })
  }

  _destroyCropper() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
  }

  // Live-update the header (top-right) company logo so the change shows
  // without a refresh. Handles all three outcomes: logo replaced, logo added
  // where there was none (header showed the company name), and logo removed
  // (header falls back to the company name).
  _updateUI(name) {
    const wrap = document.getElementById("header-company-logo")
    const href = wrap?.querySelector("a")?.getAttribute("href") || "#"

    if (this._removedLogo) {
      if (wrap) {
        wrap.innerHTML = ""
        const a = document.createElement("a")
        a.href = href
        a.className = "ml-2 text-sm font-semibold text-text-heading hover:text-brand truncate max-w-[180px]"
        a.textContent = name || ""
        wrap.appendChild(a)
      }
    } else if (this._previewDataUrl) {
      // Replace in place wherever a logo img is rendered…
      document.querySelectorAll("img[data-company-logo]").forEach(el => {
        el.src = this._previewDataUrl
        el.classList.remove("opacity-0")
        el.style.display = ""
      })
      // …or build the header img when it was showing the name-only fallback.
      if (wrap && !wrap.querySelector("img")) {
        wrap.innerHTML = ""
        const a = document.createElement("a")
        a.href = href
        a.className = "ml-2 flex-shrink-0"
        const img = document.createElement("img")
        img.src = this._previewDataUrl
        img.alt = name || "Logo"
        img.className = "h-10 max-w-[140px] object-contain relative -left-[2px]"
        img.dataset.companyLogo = "true"
        a.appendChild(img)
        wrap.appendChild(a)
      }
    }

    this._removedLogo = false
    this._previewDataUrl = null
  }

  _showErrors(messages) {
    this.errorsTarget.textContent = ""
    messages.forEach(m => {
      const p = document.createElement("p")
      p.textContent = m
      this.errorsTarget.appendChild(p)
    })
    this.errorsTarget.classList.remove("hidden")
  }

  _closeModal() {
    this.errorsTarget.classList.add("hidden")
    this.cropAreaTarget.classList.add("hidden")
    this._destroyCropper()
    this.dispatch("close", { bubbles: true })
  }

  _showFlash(type, message) {
    const cssType = type === "notice" ? "notice" : "alert"
    const container = document.createElement("div")
    container.className = `flash-toast-${cssType}`
    container.dataset.controller = "flash"
    container.dataset.flashDismissAfterValue = "5000"
    container.setAttribute("role", "alert")

    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = `<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>`

    const msgEl = document.createElement("p")
    msgEl.className = "flex-1 text-sm"
    msgEl.textContent = message

    container.appendChild(iconDiv)
    container.appendChild(msgEl)
    document.body.insertBefore(container, document.body.firstChild)
  }
}
